import {
  HStack,
  IconButton,
  TableColumnHeaderProps,
  Text,
  Th,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { RiCloseLine } from "react-icons/ri";
import { db, Student } from "../db";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import EditStudent from "../dialogs/EditStudent";

interface StudentHeaderProps extends TableColumnHeaderProps {
  student: Student;
  updateStudents: () => void;
}

const StudentHeader = ({
  student,
  updateStudents,
  ...props
}: StudentHeaderProps) => {
  const renameStudentDisclosure = useDisclosure();
  const deleteStudentDisclosure = useDisclosure();

  const deleteStudent = useCallback(
    async (studentId: number) => {
      try {
        await db.students.delete(studentId);
        const audioFilesToDelete = await db.audioFiles
          .filter((f) => f.studentId === studentId)
          .primaryKeys();
        await db.audioFiles.bulkDelete(audioFilesToDelete);
      } catch (err) {
        console.error(
          `Failed to delete student and associated audio files: ${err}`
        );
      }
      await updateStudents();
    },
    [updateStudents]
  );

  return (
    <>
      <Th
        px={2}
        py={1}
        textTransform="none"
        minWidth="10em"
        textAlign="center"
        fontSize="md"
        bgColor="gray.50"
        {...props}
      >
        <HStack justifyContent="space-between" width="100%">
          <Tooltip label="Rename student" placement="top" hasArrow={true}>
            <Text
              onClick={renameStudentDisclosure.onOpen}
              cursor="pointer"
              flex={1}
            >
              {student.name}
            </Text>
          </Tooltip>
          <Tooltip label="Delete student" placement="top" hasArrow={true}>
            <IconButton
              colorScheme="red"
              size="xs"
              variant="ghost"
              onClick={deleteStudentDisclosure.onOpen}
              aria-label="Delete student"
              icon={<RiCloseLine />}
            />
          </Tooltip>
        </HStack>
      </Th>
      {renameStudentDisclosure.isOpen && (
        <EditStudent
          student={student}
          isOpen={renameStudentDisclosure.isOpen}
          onClose={renameStudentDisclosure.onClose}
          updateStudents={updateStudents}
        />
      )}
      {deleteStudentDisclosure.isOpen && (
        <ConfirmDialog
          isOpen={deleteStudentDisclosure.isOpen}
          onClose={deleteStudentDisclosure.onClose}
          title={`Delete student (${student.name})`}
          message='Deleting this student will also delete their associated audio data. Please click "confirm" to continue.'
          onConfirm={() => {
            deleteStudent(student.id!);
            deleteStudentDisclosure.onClose();
          }}
        />
      )}
    </>
  );
};

export default StudentHeader;
